/* istanbul ignore file */
import React, { useEffect } from 'react';
import { Provider, useRollbar } from '@rollbar/react';
import { useSWRConfig } from 'swr';
import type Rollbar from 'rollbar';

const env = import.meta.env || process.env;
const isDev = env?.VITE_ENV === 'local';

const rollbarConfig = {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  accessToken: env.VITE_ROLLBAR_CLIENT_TOKEN,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  environment: env.VITE_ENV,
};

function getUrlContext() {
  const { pathname } = window.location;
  // e.g. /clients/1417/business_info → clientId: 1417, activeTab: business_info
  const clientMatch = pathname.match(/\/clients\/(\d+)(?:\/([^/]+))?/);
  return {
    pathname,
    clientIdFromUrl: clientMatch ? Number(clientMatch[1]) : null,
    activeTabFromUrl: clientMatch?.[2] ?? null,
  };
}

function getUserContext() {
  try {
    const raw = localStorage.getItem('User');
    if (!raw) return { hasUser: false };
    const user = JSON.parse(raw) as {
      id?: number;
      role?: string;
      isAdmin?: boolean;
      isSuperAdmin?: boolean;
      isClient?: boolean;
    };
    return {
      hasUser: true,
      userId: user.id,
      role: user.role,
      isAdmin: user.isAdmin,
      isSuperAdmin: user.isSuperAdmin,
      isClient: user.isClient,
    };
  } catch {
    return { hasUser: false, userParseError: true };
  }
}

function getDeviceContext() {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { effectiveType?: string };
  };
  const perf = performance as Performance & {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  };
  return {
    deviceMemoryGB: nav.deviceMemory ?? null,
    hardwareConcurrency: navigator.hardwareConcurrency,
    connectionType: nav.connection?.effectiveType ?? null,
    jsHeap: perf.memory
      ? {
          usedMB: Math.round(perf.memory.usedJSHeapSize / 1024 / 1024),
          totalMB: Math.round(perf.memory.totalJSHeapSize / 1024 / 1024),
          limitMB: Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024),
        }
      : null,
  };
}

function getLocalContext() {
  let storageContext: {
    impersonatingAccountId: string | null;
    companyName: string | null;
    hasToken: boolean;
  };
  try {
    storageContext = {
      impersonatingAccountId: localStorage.getItem('impersonatingAccountId'),
      companyName: localStorage.getItem('companyName'),
      hasToken: Boolean(localStorage.getItem('access_token')),
    };
  } catch {
    storageContext = {
      impersonatingAccountId: null,
      companyName: null,
      hasToken: false,
    };
  }
  return {
    url: window.location.href,
    ...storageContext,
    ...getUrlContext(),
    ...getUserContext(),
    device: getDeviceContext(),
  };
}

interface BoundaryProps {
  children: React.ReactNode;
  rollbar: Rollbar;
}
interface BoundaryState {
  error: Error | null;
}

class TopLevelErrorBoundary extends React.Component<
  BoundaryProps,
  BoundaryState
> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const context = getLocalContext();
    const { rollbar } = this.props;
    if (isDev) {
      /* eslint-disable no-console */
      console.log('[ErrorBoundary] Caught error:', error.message);
      console.log('[ErrorBoundary] Stack:', error.stack);
      console.log('[ErrorBoundary] Component tree:', info.componentStack);
      console.log('[ErrorBoundary] Context:', context);
      /* eslint-enable no-console */
    }
    rollbar.error(error, { componentStack: info.componentStack, ...context });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
          <h2>Something went wrong</h2>
          <p>
            Please refresh the page. If the problem persists, contact support.
          </p>
          <button type='button' onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      );
    }
    return children;
  }
}

function BoundaryWithRollbar({ children }: { children: React.ReactNode }) {
  const rollbar = useRollbar();
  const { cache } = useSWRConfig();

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      if (
        event.error instanceof RangeError &&
        event.error.message.includes('Maximum call stack size exceeded')
      ) {
        const context = getLocalContext();
        const swrCacheKeys = Array.from(cache.keys()).slice(0, 50);
        rollbar.error(
          '[MaxCallStack] window error handler capture',
          event.error,
          {
            rawStack: event.error.stack || '(empty — stack overflowed)',
            swrCacheKeys,
            ...context,
          }
        );
      }
    };

    window.addEventListener('error', handler, true);
    return () => window.removeEventListener('error', handler, true);
  }, [rollbar, cache]);

  return (
    <TopLevelErrorBoundary rollbar={rollbar}>{children}</TopLevelErrorBoundary>
  );
}

export default function ErrorReporter({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <Provider config={rollbarConfig}>
      <BoundaryWithRollbar>{children}</BoundaryWithRollbar>
    </Provider>
  );
}
